import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, TextField, InputAdornment, MenuItem, Chip, Skeleton, Button,
  Table, TableHead, TableRow, TableCell, TableBody, useTheme, useMediaQuery, alpha,
  IconButton, Tooltip, ToggleButton, ToggleButtonGroup, LinearProgress,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import ViewListRoundedIcon from "@mui/icons-material/ViewListRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import { useGetMyStudentsQuery } from "../../Api/attoDeskApi";

/**
 * Staff Portal → My Students (Phase 3a).
 *
 * Reads /api/staff-portal/my-students — a self-only endpoint. Every
 * student in the response is one this teacher currently teaches via
 * an active occurrence assignment (StaffTeachingScopeService rule).
 * No admin data (finance / credentials / other-teacher notes) is
 * available even if the URL is manipulated.
 *
 * Backend DTO fields relied on below:
 *   student_id, student_name, grade, avatar_initials,
 *   subjects_with_me[], classes_with_me_count,
 *   next_class { occurrence_id, scheduled_start_at, class_label, location } | null,
 *   attendance_summary { present, absent, late, rate } | null
 *
 * ONE roster request feeds every card + KPI — no per-student fan-out.
 */

interface StudentRow {
  student_id: number;
  student_name: string;
  grade: string | null;
  avatar_initials: string;
  subjects_with_me: string[];
  classes_with_me_count: number;
  next_class: { occurrence_id: number; scheduled_start_at: string; class_label: string; location: string | null } | null;
  attendance_summary: { present: number; absent: number; late: number; rate: number } | null;
}

type ViewMode = "cards" | "table";
type AttendanceFilter = "all" | "excellent" | "good" | "watch" | "none";

const NAVY = "#0f2c4a";
const NAVY_SOFT = "#1b3a6b";
const BLUE = "#3b82f6";
const TEAL = "#14b8a6";
const GREEN = "#16a34a";
const AMBER = "#f59e0b";
const RED = "#ef4444";
const LAVENDER = "#8b5cf6";

// Attendance semantics — same thresholds the spec calls out. `null`
// attendance_summary (or zero marked sessions) → "no attendance yet".
const attendanceStatus = (a: StudentRow["attendance_summary"]) => {
  if (!a || (a.present + a.absent + a.late) === 0) {
    return { level: "none" as const, label: "No attendance yet", color: "#94a3b8", tint: "#f1f5f9" };
  }
  const r = a.rate;
  if (r >= 90) return { level: "excellent" as const, label: "Excellent", color: GREEN, tint: alpha(GREEN, 0.1) };
  if (r >= 80) return { level: "good" as const, label: "Good", color: TEAL, tint: alpha(TEAL, 0.1) };
  return { level: "watch" as const, label: "Needs attention", color: AMBER, tint: alpha(AMBER, 0.12) };
};

const isSameDay = (iso: string, day: Date) => {
  const d = new Date(iso);
  return d.getFullYear() === day.getFullYear()
      && d.getMonth() === day.getMonth()
      && d.getDate() === day.getDate();
};

const fmtNextDate = (iso: string): string => {
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  const dt = new Date(iso); dt.setHours(0, 0, 0, 0);
  if (dt.getTime() === today.getTime()) return "Today";
  if (dt.getTime() === tomorrow.getTime()) return "Tomorrow";
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
};

const fmtNextTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

const KpiCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: string;
  icon: React.ReactNode;
  accent: string;
  loading?: boolean;
}> = ({ label, value, sub, icon, accent, loading }) => (
  <Box sx={{
    bgcolor: "#fff", borderRadius: "14px", border: "1px solid #e2e8f0",
    p: 2, display: "flex", alignItems: "flex-start", gap: 1.5, minHeight: 92,
    transition: "border-color 0.15s, box-shadow 0.15s",
    "&:hover": { borderColor: alpha(accent, 0.35) },
  }}>
    <Box sx={{
      width: 36, height: 36, borderRadius: "10px", flexShrink: 0,
      bgcolor: alpha(accent, 0.12), color: accent,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {icon}
    </Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography sx={{
        fontSize: 10.5, fontWeight: 700, color: "text.secondary",
        letterSpacing: "0.5px", textTransform: "uppercase",
      }}>
        {label}
      </Typography>
      <Typography sx={{
        fontSize: 24, fontWeight: 800, color: NAVY, lineHeight: 1.1, mt: 0.25,
        fontVariantNumeric: "tabular-nums",
      }}>
        {loading ? <Skeleton width={40} /> : value}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: 11, color: "text.disabled", mt: 0.25 }}>
          {sub}
        </Typography>
      )}
    </Box>
  </Box>
);

const StudentCard: React.FC<{
  row: StudentRow;
  onOpen: () => void;
  onMessage: () => void;
  onReportCard: () => void;
}> = ({ row, onOpen, onMessage, onReportCard }) => {
  const att = attendanceStatus(row.attendance_summary);
  const needsAttention = att.level === "watch";
  const noAttendance = att.level === "none";
  const isToday = row.next_class && isSameDay(row.next_class.scheduled_start_at, new Date());
  const pct = row.attendance_summary?.rate ?? 0;

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <Box
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
      sx={{
        bgcolor: "#fff", borderRadius: "16px", border: "1px solid #e2e8f0",
        p: 2, display: "flex", flexDirection: "column", gap: 1.25,
        cursor: "pointer", position: "relative", overflow: "hidden",
        transition: "transform 0.15s, box-shadow 0.15s, border-color 0.15s",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 8px 22px rgba(15,44,74,0.08)",
          borderColor: alpha(BLUE, 0.35),
        },
        "&:focus-visible": {
          outline: `2px solid ${BLUE}`, outlineOffset: 2,
        },
      }}
    >
      {(needsAttention || noAttendance) && (
        <Box sx={{
          position: "absolute", top: 12, right: 12,
          bgcolor: needsAttention ? alpha(AMBER, 0.14) : "#f1f5f9",
          color: needsAttention ? "#92400e" : "#475569",
          fontSize: 10, fontWeight: 800, letterSpacing: "0.4px",
          textTransform: "uppercase", px: 1, py: 0.25, borderRadius: "999px",
        }}>
          {needsAttention ? "Needs attention" : "No attendance yet"}
        </Box>
      )}

      {/* Header row */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, pr: (needsAttention || noAttendance) ? 12 : 0 }}>
        <Box sx={{
          width: 44, height: 44, borderRadius: "50%",
          bgcolor: alpha(BLUE, 0.12), color: NAVY_SOFT,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontWeight: 800, fontSize: 15, flexShrink: 0,
        }}>
          {row.avatar_initials || row.student_name.charAt(0).toUpperCase() || "?"}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontSize: 15, fontWeight: 700, color: NAVY, lineHeight: 1.2,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {row.student_name}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.25 }}>
            {row.grade && (
              <Chip
                label={row.grade}
                size="small"
                sx={{
                  height: 18, fontSize: 10.5, fontWeight: 700,
                  bgcolor: alpha(LAVENDER, 0.1), color: "#5b21b6",
                  "& .MuiChip-label": { px: 0.75 },
                }}
              />
            )}
            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
              {row.classes_with_me_count} class{row.classes_with_me_count === 1 ? "" : "es"} with me
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Subjects */}
      {row.subjects_with_me.length > 0 && (
        <Box>
          <Typography sx={{
            fontSize: 10, fontWeight: 700, color: "text.disabled",
            letterSpacing: "0.5px", textTransform: "uppercase", mb: 0.5,
          }}>
            My Subjects
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {row.subjects_with_me.map((s) => (
              <Chip
                key={s}
                label={s}
                size="small"
                sx={{
                  height: 22, fontSize: 11, fontWeight: 600,
                  bgcolor: alpha(BLUE, 0.08), color: NAVY_SOFT,
                  "& .MuiChip-label": { px: 1 },
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Performance strip */}
      <Box sx={{
        display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mt: "auto",
        bgcolor: "#f8fafc", borderRadius: "10px", p: 1.25,
        border: "1px solid #f1f5f9",
      }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{
            fontSize: 9.5, fontWeight: 700, color: "text.disabled",
            letterSpacing: "0.4px", textTransform: "uppercase",
          }}>
            Attendance
          </Typography>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, mt: 0.25 }}>
            <Typography sx={{
              fontSize: 16, fontWeight: 800, color: att.color, lineHeight: 1,
              fontVariantNumeric: "tabular-nums",
            }}>
              {noAttendance ? "—" : `${pct}%`}
            </Typography>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: att.color, whiteSpace: "nowrap" }}>
              {att.label}
            </Typography>
          </Box>
          {!noAttendance && (
            <LinearProgress
              variant="determinate"
              value={Math.min(pct, 100)}
              sx={{
                mt: 0.5, height: 4, borderRadius: 2, bgcolor: "#e2e8f0",
                "& .MuiLinearProgress-bar": { bgcolor: att.color },
              }}
            />
          )}
        </Box>
        <Box sx={{ minWidth: 0, borderLeft: "1px solid #e2e8f0", pl: 1.25 }}>
          <Typography sx={{
            fontSize: 9.5, fontWeight: 700, color: "text.disabled",
            letterSpacing: "0.4px", textTransform: "uppercase",
          }}>
            Next Class
          </Typography>
          {row.next_class ? (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
                <CalendarTodayRoundedIcon sx={{ fontSize: 12, color: isToday ? BLUE : "#64748b" }} />
                <Typography sx={{
                  fontSize: 12.5, fontWeight: 700, color: isToday ? BLUE : NAVY,
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>
                  {fmtNextDate(row.next_class.scheduled_start_at)}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.25 }}>
                {fmtNextTime(row.next_class.scheduled_start_at)}
              </Typography>
              <Typography sx={{
                fontSize: 10.5, color: "text.disabled", mt: 0.25,
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}>
                {row.next_class.class_label}
              </Typography>
            </>
          ) : (
            <Typography sx={{ fontSize: 12, color: "text.disabled", mt: 0.25 }}>
              No upcoming class
            </Typography>
          )}
        </Box>
      </Box>

      {/* Actions */}
      <Box sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 1, borderTop: "1px solid #f1f5f9", pt: 1.25,
      }}>
        <Button
          onClick={(e) => { stop(e); onOpen(); }}
          variant="contained"
          size="small"
          endIcon={<ArrowForwardIosIcon sx={{ fontSize: "10px !important" }} />}
          sx={{
            textTransform: "none", fontWeight: 700, fontSize: 12.5,
            bgcolor: NAVY, "&:hover": { bgcolor: NAVY_SOFT },
            boxShadow: "none", borderRadius: "8px", px: 1.5,
          }}
        >
          View Profile
        </Button>
        <Box sx={{ display: "flex", gap: 0.25 }}>
          <Tooltip title="Report Card">
            <IconButton
              size="small"
              onClick={(e) => { stop(e); onReportCard(); }}
              sx={{ color: "#64748b", "&:hover": { bgcolor: alpha(TEAL, 0.1), color: TEAL } }}
            >
              <AssignmentIndOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Messages">
            <IconButton
              size="small"
              onClick={(e) => { stop(e); onMessage(); }}
              sx={{ color: "#64748b", "&:hover": { bgcolor: alpha(BLUE, 0.1), color: BLUE } }}
            >
              <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

const MyStudents: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const { data, isLoading, isError, refetch, isFetching } = useGetMyStudentsQuery();

  const rows: StudentRow[] = useMemo(() => {
    const raw = (data as any)?.data;
    return Array.isArray(raw) ? raw : [];
  }, [data]);

  const [search, setSearch] = useState("");
  const [gradeFilter, setGradeFilter] = useState<string>("");
  const [subjectFilter, setSubjectFilter] = useState<string>("");
  const [attendanceFilter, setAttendanceFilter] = useState<AttendanceFilter>("all");
  const [viewMode, setViewMode] = useState<ViewMode>("cards");

  const grades = useMemo(
    () => Array.from(new Set(rows.map((r) => r.grade).filter(Boolean))).sort() as string[],
    [rows]
  );
  const subjects = useMemo(
    () => Array.from(new Set(rows.flatMap((r) => r.subjects_with_me))).sort(),
    [rows]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (q && !r.student_name.toLowerCase().includes(q)) return false;
      if (gradeFilter && r.grade !== gradeFilter) return false;
      if (subjectFilter && !r.subjects_with_me.includes(subjectFilter)) return false;
      if (attendanceFilter !== "all") {
        const s = attendanceStatus(r.attendance_summary).level;
        if (s !== attendanceFilter) return false;
      }
      return true;
    });
  }, [rows, search, gradeFilter, subjectFilter, attendanceFilter]);

  // ── KPIs — all from the single roster response, no fan-out queries ──
  const totalStudents = rows.length;
  const attentionCount = useMemo(
    () => rows.filter((r) => attendanceStatus(r.attendance_summary).level === "watch").length,
    [rows]
  );
  const upcomingTodayCount = useMemo(() => {
    const today = new Date();
    return rows.filter((r) => r.next_class && isSameDay(r.next_class.scheduled_start_at, today)).length;
  }, [rows]);
  const subjectsTaught = subjects.length;

  const activeFilterCount =
    (search ? 1 : 0) + (gradeFilter ? 1 : 0) + (subjectFilter ? 1 : 0) + (attendanceFilter !== "all" ? 1 : 0);
  const clearFilters = () => {
    setSearch(""); setGradeFilter(""); setSubjectFilter(""); setAttendanceFilter("all");
  };

  const openStudent = (id: number) => navigate(`/staff-portal/students/${id}`);
  const openReportCard = (id: number) => navigate(`/staff-portal/reports/report-cards?student_id=${id}`);
  const openMessages = () => navigate(`/staff-portal/messages`);

  const gridSx = {
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      sm: "1fr 1fr",
      lg: "repeat(auto-fit, minmax(360px, 1fr))",
    },
    gap: 2,
  };

  return (
    <Box sx={{ bgcolor: "#f6f8fc", minHeight: "100vh", pb: 5 }}>
      <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, pt: { xs: 2, md: 3 }, display: "flex", flexDirection: "column", gap: 2 }}>

        {/* ── Premium page header ───────────────────────────── */}
        <Box sx={{
          borderRadius: "16px",
          border: "1px solid #dbe4f1",
          background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 55%, #eef4fb 100%)",
          px: { xs: 2, md: 3 }, py: { xs: 2, md: 2.25 },
          display: "flex", alignItems: "center", gap: 2,
          flexWrap: "wrap",
        }}>
          <Box sx={{
            width: 46, height: 46, borderRadius: "12px", flexShrink: 0,
            bgcolor: alpha(BLUE, 0.12), color: NAVY_SOFT,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <GroupsRoundedIcon sx={{ fontSize: 26 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{
              fontSize: 10.5, fontWeight: 800, color: "#64748b",
              letterSpacing: "0.8px", textTransform: "uppercase",
            }}>
              Staff Portal
            </Typography>
            <Typography sx={{
              fontSize: { xs: 20, md: 22 }, fontWeight: 800, color: NAVY, lineHeight: 1.15,
              letterSpacing: "-0.01em",
            }}>
              My Students
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: "#64748b", mt: 0.25 }}>
              Students currently assigned to your classes.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
            <Chip
              label={`${totalStudents} ${totalStudents === 1 ? "Student" : "Students"}`}
              sx={{
                height: 28, fontWeight: 700, fontSize: 12.5,
                bgcolor: "#fff", color: NAVY, border: "1px solid #dbe4f1",
              }}
            />
            <Tooltip title="Refresh students">
              {/*
                Manual roster refresh — hits the same
                /staff-portal/my-students endpoint the initial load used
                (no N+1 fan-out). RTK Query keeps the previously-fetched
                data visible while `isFetching` is true, so existing
                cards stay on screen; only the button flips to a
                spinning disabled state. `aria-label` mirrors the
                tooltip so screen-reader users get the same affordance.
              */}
              <span>
                <IconButton
                  onClick={() => refetch()}
                  disabled={isFetching}
                  aria-label="Refresh students"
                  sx={{
                    bgcolor: "#fff", border: "1px solid #dbe4f1", borderRadius: "8px",
                    width: 34, height: 34, color: NAVY_SOFT,
                    "&:hover": { bgcolor: alpha(BLUE, 0.06) },
                    "&.Mui-disabled": { color: NAVY_SOFT, opacity: 0.75 },
                    "@keyframes staffRosterSpin": {
                      from: { transform: "rotate(0deg)" },
                      to:   { transform: "rotate(360deg)" },
                    },
                  }}
                >
                  <RefreshRoundedIcon
                    sx={{
                      fontSize: 18,
                      animation: isFetching ? "staffRosterSpin 0.9s linear infinite" : "none",
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* ── KPI overview ──────────────────────────────────── */}
        <Box sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
          gap: 1.5,
        }}>
          <KpiCard
            label="Total Students"
            value={totalStudents}
            sub="in your scope"
            icon={<GroupsRoundedIcon sx={{ fontSize: 20 }} />}
            accent={BLUE}
            loading={isLoading}
          />
          <KpiCard
            label="Attendance Watch"
            value={attentionCount}
            sub="below 80%"
            icon={<WarningAmberRoundedIcon sx={{ fontSize: 20 }} />}
            accent={attentionCount > 0 ? AMBER : GREEN}
            loading={isLoading}
          />
          <KpiCard
            label="Upcoming Today"
            value={upcomingTodayCount}
            sub={upcomingTodayCount === 1 ? "class today" : "classes today"}
            icon={<EventAvailableRoundedIcon sx={{ fontSize: 20 }} />}
            accent={TEAL}
            loading={isLoading}
          />
          <KpiCard
            label="Subjects Taught"
            value={subjectsTaught}
            sub="across roster"
            icon={<MenuBookRoundedIcon sx={{ fontSize: 20 }} />}
            accent={LAVENDER}
            loading={isLoading}
          />
        </Box>

        {/* ── Toolbar ──────────────────────────────────────── */}
        <Box sx={{
          bgcolor: "#fff", borderRadius: "14px", border: "1px solid #e2e8f0",
          p: 1.5, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1,
        }}>
          <TextField
            size="small"
            placeholder="Search students..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon fontSize="small" sx={{ color: "text.disabled" }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select size="small" label="Grade"
            value={gradeFilter} onChange={(e) => setGradeFilter(e.target.value)}
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="">All grades</MenuItem>
            {grades.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
          </TextField>
          <TextField
            select size="small" label="Subject"
            value={subjectFilter} onChange={(e) => setSubjectFilter(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All subjects</MenuItem>
            {subjects.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
          <TextField
            select size="small" label="Attendance"
            value={attendanceFilter} onChange={(e) => setAttendanceFilter(e.target.value as AttendanceFilter)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="excellent">Excellent (≥ 90%)</MenuItem>
            <MenuItem value="good">Good (80–89%)</MenuItem>
            <MenuItem value="watch">Watch (&lt; 80%)</MenuItem>
            <MenuItem value="none">No attendance yet</MenuItem>
          </TextField>
          {activeFilterCount > 0 && (
            <Button
              size="small"
              onClick={clearFilters}
              sx={{ textTransform: "none", fontWeight: 700, color: NAVY_SOFT }}
            >
              Reset
            </Button>
          )}
          <Box sx={{ flexShrink: 0, ml: "auto" }}>
            <ToggleButtonGroup
              exclusive
              value={viewMode}
              onChange={(_, v) => v && setViewMode(v)}
              size="small"
              sx={{
                "& .MuiToggleButton-root": {
                  border: "1px solid #e2e8f0", px: 1.25, py: 0.5,
                  color: "#64748b",
                  "&.Mui-selected": { bgcolor: alpha(BLUE, 0.1), color: BLUE, borderColor: alpha(BLUE, 0.3) },
                },
              }}
            >
              <ToggleButton value="cards" aria-label="Card view">
                <GridViewRoundedIcon sx={{ fontSize: 16 }} />
              </ToggleButton>
              <ToggleButton value="table" aria-label="Table view">
                <ViewListRoundedIcon sx={{ fontSize: 16 }} />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* ── Body ─────────────────────────────────────────── */}
        {isLoading ? (
          <Box sx={gridSx}>
            {[0, 1, 2].map((i) => (
              <Box key={i} sx={{
                bgcolor: "#fff", borderRadius: "16px", border: "1px solid #e2e8f0",
                p: 2, minHeight: 240,
              }}>
                <Skeleton variant="rectangular" width={44} height={44} sx={{ borderRadius: "50%", mb: 1 }} />
                <Skeleton width="70%" height={22} />
                <Skeleton width="40%" height={18} sx={{ mt: 0.5 }} />
                <Skeleton width="100%" height={40} sx={{ mt: 1.5 }} />
                <Skeleton width="100%" height={60} sx={{ mt: 1.5 }} />
              </Box>
            ))}
          </Box>
        ) : isError ? (
          <Box sx={{
            bgcolor: "#fff", borderRadius: "16px", border: "1px solid #fecaca",
            textAlign: "center", py: 5, px: 3,
          }}>
            <WarningAmberRoundedIcon sx={{ fontSize: 44, color: RED, mb: 1 }} />
            <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: 15 }}>
              We couldn't load your students.
            </Typography>
            <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.5, mb: 2 }}>
              Please try again.
            </Typography>
            <Button
              variant="contained"
              startIcon={<ReplayRoundedIcon />}
              onClick={() => refetch()}
              sx={{ textTransform: "none", fontWeight: 700, bgcolor: NAVY, "&:hover": { bgcolor: NAVY_SOFT }, boxShadow: "none" }}
            >
              Retry
            </Button>
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{
            bgcolor: "#fff", borderRadius: "16px",
            border: `1px dashed ${alpha(BLUE, 0.3)}`,
            textAlign: "center", py: 6, px: 3,
          }}>
            <Box sx={{
              width: 64, height: 64, borderRadius: "50%",
              bgcolor: alpha(TEAL, 0.1), color: TEAL,
              display: "inline-flex", alignItems: "center", justifyContent: "center", mb: 1.5,
            }}>
              <PersonSearchOutlinedIcon sx={{ fontSize: 32 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: NAVY }}>
              No students assigned yet
            </Typography>
            <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.5, maxWidth: 380, mx: "auto" }}>
              Students will appear here once they are assigned to one of your classes.
            </Typography>
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{
            bgcolor: "#fff", borderRadius: "16px", border: "1px solid #e2e8f0",
            textAlign: "center", py: 5, px: 3,
          }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: NAVY }}>
              No students match your filters
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.5 }}>
              Try another grade, subject, or clear your search.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={clearFilters}
              sx={{ mt: 1.5, textTransform: "none", fontWeight: 700, borderRadius: "8px" }}
            >
              Clear Filters
            </Button>
          </Box>
        ) : viewMode === "cards" || isNarrow ? (
          <Box sx={gridSx}>
            {filtered.map((r) => (
              <StudentCard
                key={r.student_id}
                row={r}
                onOpen={() => openStudent(r.student_id)}
                onReportCard={() => openReportCard(r.student_id)}
                onMessage={() => openMessages()}
              />
            ))}
          </Box>
        ) : (
          /* Premium table view */
          <Box sx={{ bgcolor: "#fff", borderRadius: "14px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: NAVY }}>
                  {["Student", "Grade", "My Subjects", "Next Class", "Attendance", "Status", ""].map((h) => (
                    <TableCell
                      key={h}
                      align={h === "" ? "right" : "left"}
                      sx={{
                        fontSize: 10.5, fontWeight: 800, color: "#fff",
                        letterSpacing: "0.5px", textTransform: "uppercase",
                        borderBottom: "none", py: 1.25,
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((r) => {
                  const att = attendanceStatus(r.attendance_summary);
                  const isToday = r.next_class && isSameDay(r.next_class.scheduled_start_at, new Date());
                  return (
                    <TableRow
                      key={r.student_id}
                      hover
                      onClick={() => openStudent(r.student_id)}
                      sx={{ cursor: "pointer", height: 60 }}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                          <Box sx={{
                            width: 34, height: 34, borderRadius: "50%",
                            bgcolor: alpha(BLUE, 0.12), color: NAVY_SOFT,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontWeight: 800, fontSize: 12, flexShrink: 0,
                          }}>
                            {r.avatar_initials || "?"}
                          </Box>
                          <Typography sx={{ fontWeight: 700, fontSize: 13.5, color: NAVY }}>
                            {r.student_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: 12.5 }}>{r.grade || "—"}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {r.subjects_with_me.length > 0
                            ? r.subjects_with_me.slice(0, 3).map((s) => (
                              <Chip key={s} label={s} size="small" sx={{
                                height: 20, fontSize: 10.5,
                                bgcolor: alpha(BLUE, 0.08), color: NAVY_SOFT,
                              }} />
                            ))
                            : <Typography sx={{ fontSize: 12, color: "text.disabled" }}>—</Typography>}
                          {r.subjects_with_me.length > 3 && (
                            <Chip label={`+${r.subjects_with_me.length - 3}`} size="small"
                              sx={{ height: 20, fontSize: 10.5 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {r.next_class ? (
                          <Box>
                            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: isToday ? BLUE : NAVY }}>
                              {fmtNextDate(r.next_class.scheduled_start_at)} · {fmtNextTime(r.next_class.scheduled_start_at)}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                              {r.next_class.class_label}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize: 12, color: "text.disabled" }}>No upcoming class</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {att.level === "none" ? (
                          <Typography sx={{ fontSize: 12, color: "text.disabled" }}>—</Typography>
                        ) : (
                          <Typography sx={{
                            fontWeight: 800, color: att.color, fontVariantNumeric: "tabular-nums",
                          }}>
                            {r.attendance_summary!.rate}%
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={att.label}
                          size="small"
                          sx={{
                            height: 22, fontSize: 11, fontWeight: 700,
                            bgcolor: att.tint, color: att.color,
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          endIcon={<ArrowForwardIosIcon sx={{ fontSize: "12px !important" }} />}
                          onClick={(e) => { e.stopPropagation(); openStudent(r.student_id); }}
                          sx={{ textTransform: "none", fontWeight: 700, color: NAVY }}
                        >
                          View Profile
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MyStudents;
